import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search, Radio, MapPin, ChevronLeft, ChevronRight,
  Headphones, Star, Wifi, Music, ArrowRight,
  Play, Pause, Volume2, VolumeX, X, Shield,
  ThumbsUp, ThumbsDown, Share2, ListMusic, ChevronDown
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import RelatedLinks from "../components/RelatedLinks";
import { useAuth, BACKEND_URL } from "../context/AuthContext";

const API = BACKEND_URL;

const SOCIAL_ICONS = {
  instagram: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  twitter: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  facebook: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  youtube: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  tiktok: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
};

const shareStation = async (station) => {
  const url = `${window.location.origin}/directory?station=${station.id}`;
  const text = `Check out ${station.station_name} on DJsandMCMedia Radio Directory!`;
  if (navigator.share) {
    try { await navigator.share({ title: station.station_name, text, url }); } catch {}
  } else {
    try { await navigator.clipboard.writeText(url); alert("Link copied to clipboard!"); }
    catch { window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank"); }
  }
};

/* ─── Station Card (Grid) ─── */
const StationCard = ({ station, isPlaying, onPlay, myVote, voteCounts, onVote }) => (
  <div data-testid={`directory-card-${station.id}`} className="group">
    <div className="relative w-[190px] h-[190px] rounded-xl overflow-hidden mb-3 cursor-pointer" onClick={() => onPlay(station)}>
      {station.logo_url ? (
        <img src={station.logo_url} alt={station.station_name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy"
          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
      ) : null}
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/80 to-indigo-900/80"
        style={{ display: station.logo_url ? "none" : "flex" }}>
        <Radio className="w-12 h-12 text-white/30" />
      </div>
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-200 flex items-center justify-center">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg shadow-emerald-500/30 ${
          isPlaying ? "bg-emerald-500 scale-100 opacity-100" : "bg-emerald-500 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100"
        }`}>
          {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Headphones className="w-6 h-6 text-white" />}
        </div>
      </div>
      {station.featured && (
        <span className="absolute top-2 left-2 bg-amber-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 backdrop-blur-sm">
          <Star className="w-3 h-3" /> FEATURED
        </span>
      )}
      {station.genre?.length > 0 && (
        <span className="absolute bottom-2 left-2 bg-black/60 text-white/90 text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">
          {Array.isArray(station.genre) ? station.genre.join(", ") : station.genre}
        </span>
      )}
    </div>
    <p className="text-white text-sm font-semibold truncate leading-tight">{station.station_name}</p>
    {station.location && (
      <span className="flex items-center gap-0.5 text-white/40 text-xs truncate mt-1">
        <MapPin className="w-3 h-3 flex-shrink-0" /> {station.location}
      </span>
    )}
    <span className="text-emerald-400/50 text-[10px] font-medium mt-0.5 block truncate">Part of The NU Vibe Network</span>
    <div className="flex items-center gap-1 mt-2">
      <button onClick={(e) => { e.stopPropagation(); onVote(station.id, "up"); }}
        className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] transition ${myVote === "up" ? "text-green-400 bg-green-500/10" : "text-white/20 hover:text-green-400 hover:bg-white/5"}`}
        data-testid={`card-vote-up-${station.id}`}>
        <ThumbsUp className="w-3 h-3" />{(voteCounts?.up || 0) > 0 && <span>{voteCounts.up}</span>}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onVote(station.id, "down"); }}
        className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] transition ${myVote === "down" ? "text-red-400 bg-red-500/10" : "text-white/20 hover:text-red-400 hover:bg-white/5"}`}
        data-testid={`card-vote-down-${station.id}`}>
        <ThumbsDown className="w-3 h-3" />{(voteCounts?.down || 0) > 0 && <span>{voteCounts.down}</span>}
      </button>
      <button onClick={(e) => { e.stopPropagation(); shareStation(station); }}
        className="text-white/20 hover:text-white/60 p-1 rounded-full hover:bg-white/5 transition ml-auto"
        data-testid={`card-share-${station.id}`}>
        <Share2 className="w-3 h-3" />
      </button>
    </div>
  </div>
);

/* ─── Featured Carousel ─── */
const FeaturedCarousel = ({ stations, playingId, onPlay, myVotes, allVoteCounts, onVote }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    return () => el.removeEventListener("scroll", updateArrows);
  }, [stations, updateArrows]);
  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 600, behavior: "smooth" });
  if (!stations?.length) return null;
  return (
    <section className="mb-10" data-testid="featured-carousel">
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-400" />
          <h2 className="text-white text-lg sm:text-xl font-bold tracking-tight">Featured Stations</h2>
        </div>
        <div className="flex gap-1">
          {canScrollLeft && <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"><ChevronLeft className="w-4 h-4 text-white" /></button>}
          {canScrollRight && <button onClick={() => scroll(1)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"><ChevronRight className="w-4 h-4 text-white" /></button>}
        </div>
      </div>
      <div ref={scrollRef} className="flex gap-5 overflow-x-auto pb-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {stations.map((s) => (
          <div key={s.id} className="flex-shrink-0 w-[200px] sm:w-[220px]">
            <StationCard station={s} isPlaying={playingId === s.id} onPlay={onPlay} myVote={myVotes[s.id]} voteCounts={allVoteCounts[s.id]} onVote={onVote} />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Directory Mini Player ─── */
const DirectoryMiniPlayer = ({ station, playing, audioRef, onToggle, onClose, myVote, voteCounts, onVote }) => {
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [liveSeconds, setLiveSeconds] = useState(0);
  const [socialLinks, setSocialLinks] = useState({});
  const [weather, setWeather] = useState(null);
  const [infoSlide, setInfoSlide] = useState(0);
  const [clockTime, setClockTime] = useState("");
  const [nowPlaying, setNowPlaying] = useState(null);
  const [songHistory, setSongHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => { fetch(`${API}/api/public/social-links`).then(r => r.ok ? r.json() : {}).then(setSocialLinks).catch(() => {}); }, []);
  useEffect(() => {
    fetch("https://wttr.in/?format=j1").then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      const cur = d.current_condition?.[0]; const area = d.nearest_area?.[0];
      if (cur) {
        const code = parseInt(cur.weatherCode, 10); let icon = "☀️";
        if ([176,263,266,293,296,299,302,305,308,311,314,356,359].includes(code)) icon = "🌧️";
        else if ([179,227,230,323,326,329,332,335,338,368,371,374,377,392,395].includes(code)) icon = "🌨️";
        else if ([200,386,389].includes(code)) icon = "⛈️";
        else if ([116,119,122].includes(code)) icon = "☁️";
        else if (code === 248 || code === 260) icon = "🌫️";
        else if (code === 113) { const h = new Date().getHours(); icon = (h >= 6 && h < 18) ? "☀️" : "🌙"; }
        setWeather({ temp: cur.temp_F, icon, city: area?.areaName?.[0]?.value || "" });
      }
    }).catch(() => {});
  }, []);
  useEffect(() => { const u = () => setClockTime(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })); u(); const id = setInterval(u, 30000); return () => clearInterval(id); }, []);
  useEffect(() => { const id = setInterval(() => setInfoSlide(s => (s + 1) % (nowPlaying ? 4 : 3)), 5000); return () => clearInterval(id); }, [nowPlaying]);
  useEffect(() => { if (!playing) { setLiveSeconds(0); return; } const start = Date.now(); const id = setInterval(() => setLiveSeconds(Math.floor((Date.now() - start) / 1000)), 1000); return () => clearInterval(id); }, [playing, station?.id]);
  useEffect(() => { if (audioRef.current) audioRef.current.volume = muted ? 0 : volume; }, [volume, muted, audioRef]);

  // Poll stream metadata every 15s
  useEffect(() => {
    if (!station?.stream_url || !playing) { setNowPlaying(null); setSongHistory([]); setHistoryOpen(false); return; }
    const fetchMeta = () => {
      fetch(`${API}/api/directory/now-playing?stream_url=${encodeURIComponent(station.stream_url)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.now_playing) setNowPlaying(d.now_playing); })
        .catch(() => {});
      fetch(`${API}/api/directory/song-history?stream_url=${encodeURIComponent(station.stream_url)}&limit=10`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.history) setSongHistory(d.history); })
        .catch(() => {});
    };
    fetchMeta();
    const id = setInterval(fetchMeta, 15000);
    return () => clearInterval(id);
  }, [station?.stream_url, playing]);

  const fmtLive = (s) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60; return h > 0 ? `${h}h ${m}m` : `${m}:${String(sec).padStart(2, "0")}`; };

  if (!station) return null;
  const vc = voteCounts || { up: 0, down: 0 };
  const genreText = Array.isArray(station.genre) ? station.genre.join(", ") : station.genre || "";

  return (
    <div data-testid="directory-mini-player" className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0a0a12]/95 backdrop-blur-xl border-t border-white/10">
      <div className="max-w-screen-xl mx-auto flex items-center gap-3 px-4 py-2.5">
        <div className="w-11 h-11 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
          {station.logo_url ? <img src={station.logo_url} alt="" className="w-full h-full object-cover" /> :
            <div className="w-full h-full flex items-center justify-center"><Music className="w-5 h-5 text-white/20" /></div>}
        </div>
        <div className="min-w-0 flex-1 max-w-[200px]">
          <p className="text-white text-sm font-medium truncate">{station.station_name}</p>
          <p className="text-white/40 text-xs truncate">{genreText}{station.location ? ` — ${station.location}` : ""}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => onVote(station.id, "up")} className={`flex items-center gap-1 p-1.5 rounded-full transition text-xs ${myVote === "up" ? "text-green-400 bg-green-500/10" : "text-white/25 hover:text-green-400 hover:bg-white/5"}`} data-testid="player-vote-up">
            <ThumbsUp className="w-3.5 h-3.5" />{vc.up > 0 && <span>{vc.up}</span>}
          </button>
          <button onClick={() => onVote(station.id, "down")} className={`flex items-center gap-1 p-1.5 rounded-full transition text-xs ${myVote === "down" ? "text-red-400 bg-red-500/10" : "text-white/25 hover:text-red-400 hover:bg-white/5"}`} data-testid="player-vote-down">
            <ThumbsDown className="w-3.5 h-3.5" />{vc.down > 0 && <span>{vc.down}</span>}
          </button>
        </div>
        <button onClick={() => shareStation(station)} className="text-white/25 hover:text-white/60 p-1.5 rounded-full hover:bg-white/5 transition flex-shrink-0" data-testid="player-share"><Share2 className="w-3.5 h-3.5" /></button>
        {playing && liveSeconds > 0 && <span className="text-green-400 text-xs font-mono tabular-nums flex-shrink-0">{fmtLive(liveSeconds)}</span>}
        <div className="hidden sm:flex items-center gap-1.5 bg-green-500/20 border border-green-500/30 px-2 py-0.5 rounded-full flex-shrink-0">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /><span className="text-green-400 text-[10px] font-bold">LIVE</span>
        </div>
        <button onClick={onToggle} className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform" data-testid="directory-player-toggle">
          {playing ? <Pause className="w-4 h-4 text-black" /> : <Play className="w-4 h-4 text-black ml-0.5" />}
        </button>
        <div className="hidden sm:flex items-center gap-2">
          <button onClick={() => setMuted(!muted)} className="text-white/40 hover:text-white/70 transition">{muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
          <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }} className="w-20 accent-green-500 h-1" />
        </div>
        <div className="hidden sm:block w-px h-5 bg-white/[0.06] flex-shrink-0" />
        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 min-w-[160px]" data-testid="directory-player-ticker">
          <div className="overflow-hidden h-4 relative w-full">
            {nowPlaying && (
              <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === 0 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}>
                <span className="text-[11px]">🎵</span><span className="text-[11px] text-white/40 font-medium truncate max-w-[140px]">{nowPlaying}</span>
              </div>
            )}
            <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === (nowPlaying ? 1 : 0) ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}>
              {weather ? (<><span className="text-[11px]">{weather.icon}</span><span className="text-[11px] text-white/30 font-mono">{weather.temp}°F</span><span className="text-[10px] text-white/25 truncate max-w-[70px]">{weather.city}</span></>) : <span className="text-[10px] text-white/20">--</span>}
            </div>
            <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === (nowPlaying ? 2 : 1) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}><span className="text-[11px]">🕐</span><span className="text-[11px] text-white/30 font-mono">{clockTime}</span></div>
            <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === (nowPlaying ? 3 : 2) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}><span className="text-[11px]">🎧</span><span className="text-[11px] text-white/30 font-mono">Live 24/7</span></div>
          </div>
        </div>
        {/* Recently Played */}
        <div className="relative flex-shrink-0">
          <button onClick={() => setHistoryOpen(!historyOpen)}
            className={`p-1.5 rounded-full transition ${historyOpen ? "text-emerald-400 bg-emerald-500/10" : "text-white/25 hover:text-white/60 hover:bg-white/5"}`}
            data-testid="player-history-btn" title="Recently Played">
            <ListMusic className="w-4 h-4" />
          </button>
          {historyOpen && songHistory.length > 0 && (
            <div className="absolute bottom-full right-0 mb-2 w-72 max-h-64 overflow-y-auto bg-[#0e0e1a]/98 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/60 z-[80]"
              data-testid="player-history-dropdown">
              <div className="sticky top-0 bg-[#0e0e1a] px-3 py-2 border-b border-white/5 flex items-center justify-between">
                <span className="text-white/60 text-xs font-semibold tracking-wide uppercase">Recently Played</span>
                <button onClick={() => setHistoryOpen(false)} className="text-white/30 hover:text-white/60"><X className="w-3 h-3" /></button>
              </div>
              <div className="py-1">
                {songHistory.map((track, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-2 hover:bg-white/5 transition" data-testid={`history-track-${i}`}>
                    {track.art ? (
                      <img src={track.art} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 bg-white/5" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center flex-shrink-0"><Music className="w-3.5 h-3.5 text-white/15" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-white/80 text-xs font-medium truncate">{track.title || "Unknown"}</p>
                      <p className="text-white/30 text-[10px] truncate">{track.artist || "Unknown Artist"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {historyOpen && songHistory.length === 0 && (
            <div className="absolute bottom-full right-0 mb-2 w-56 bg-[#0e0e1a]/98 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/60 z-[80] p-4 text-center"
              data-testid="player-history-empty">
              <Music className="w-5 h-5 text-white/10 mx-auto mb-2" />
              <p className="text-white/30 text-xs">No history available yet</p>
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-3 flex-1 justify-end">
          {Object.entries(socialLinks).map(([key, url]) => url && SOCIAL_ICONS[key] ? <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white transition">{SOCIAL_ICONS[key]}</a> : null)}
          <Link to="/dmca" className="flex items-center gap-1.5 text-white/30 hover:text-white/60 transition text-[10px]"><Shield className="w-3.5 h-3.5" /><span>DMCA</span></Link>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white/60 transition p-1.5" data-testid="directory-player-close"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
export default function DirectoryPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allStations, setAllStations] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [genres, setGenres] = useState([]);
  const [activeGenre, setActiveGenre] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(new Audio());
  const [showPromo, setShowPromo] = useState(false);
  const promoShown = useRef(false);
  const promoTimerRef = useRef(null);

  // Auto-hide promo after 12 seconds if not dismissed
  useEffect(() => {
    if (!showPromo) return;
    const t = setTimeout(() => setShowPromo(false), 12000);
    return () => clearTimeout(t);
  }, [showPromo]);
  const promoMessages = [
    { headline: "Want your own station like this one?", sub: "Launch your own branded radio station with DJsandMCMedia." },
    { headline: "Don't have a station yet?", sub: "Your music. Your station. Your audience." },
    { headline: "Tired of social platforms muting your stream?", sub: "Launch your own radio station in minutes." },
  ];
  const [promoIndex] = useState(() => Math.floor(Math.random() * 3));

  // Show promo after 25 seconds of active listening
  useEffect(() => {
    if (isPlaying && !promoShown.current) {
      promoTimerRef.current = setTimeout(() => {
        if (!promoShown.current) {
          setShowPromo(true);
          promoShown.current = true;
        }
      }, 25000);
    }
    return () => { if (promoTimerRef.current) clearTimeout(promoTimerRef.current); };
  }, [isPlaying]);

  const [allVoteCounts, setAllVoteCounts] = useState({});
  const [myVotes, setMyVotes] = useState({});

  // Load all data
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/directory/categories`).then(r => r.json()),
      fetch(`${API}/api/directory/vote-counts`).then(r => r.ok ? r.json() : {}),
    ]).then(([catData, votes]) => {
      setFeatured(catData.featured || []);
      setGenres(catData.genre_list || []);
      // Build deduplicated "all stations" list from recent
      // (recent already has all approved stations sorted by approved_at)
      setAllStations(catData.recent || []);
      setAllVoteCounts(votes);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    fetch(`${API}/api/directory/my-votes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : {}).then(setMyVotes).catch(() => {});
  }, [user]);

  useEffect(() => { const a = audioRef.current; return () => { a.pause(); a.src = ""; }; }, []);

  const handleVote = useCallback(async (stationId, vote) => {
    if (!user) { alert("Please log in to vote"); return; }
    const token = localStorage.getItem("token");
    try {
      const r = await fetch(`${API}/api/directory/vote`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ station_id: stationId, vote }),
      });
      if (!r.ok) return;
      const d = await r.json();
      if (d.status === "removed") {
        setAllVoteCounts(p => ({ ...p, [stationId]: { ...p[stationId], [vote]: Math.max(0, (p[stationId]?.[vote] || 0) - 1) } }));
        setMyVotes(p => { const n = { ...p }; delete n[stationId]; return n; });
      } else {
        const pv = myVotes[stationId];
        setAllVoteCounts(p => {
          const c = { ...p, [stationId]: { ...(p[stationId] || { up: 0, down: 0 }) } };
          c[stationId][vote] = (c[stationId][vote] || 0) + 1;
          if (pv && pv !== vote) c[stationId][pv] = Math.max(0, (c[stationId][pv] || 0) - 1);
          return c;
        });
        setMyVotes(p => ({ ...p, [stationId]: vote }));
      }
    } catch {}
  }, [user, myVotes]);

  const stingPlayed = useRef(new Set());

  const handlePlay = useCallback((station) => {
    const audio = audioRef.current;
    if (currentStation?.id === station.id) {
      if (isPlaying) { audio.pause(); setIsPlaying(false); } else { audio.play().catch(() => setIsPlaying(false)); setIsPlaying(true); }
      return;
    }
    audio.pause();
    setCurrentStation(station);

    if (!stingPlayed.current.has(station.id)) {
      stingPlayed.current.add(station.id);
      audio.src = "/audio/nuvibe-sting.mp3";
      audio.volume = 0.5;
      setIsPlaying(true);
      audio.play().catch(() => {
        audio.src = station.stream_url; audio.volume = 0.8;
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      });
      const onEnd = () => {
        audio.removeEventListener("ended", onEnd);
        audio.src = station.stream_url; audio.volume = 0.8;
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      };
      audio.addEventListener("ended", onEnd);
    } else {
      audio.src = station.stream_url; audio.volume = 0.8;
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  }, [currentStation, isPlaying]);

  // Reset play state on audio errors
  useEffect(() => {
    const audio = audioRef.current;
    const onError = () => setIsPlaying(false);
    audio.addEventListener("error", onError);
    audio.addEventListener("stalled", onError);
    return () => { audio.removeEventListener("error", onError); audio.removeEventListener("stalled", onError); };
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); } else { audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false)); }
  }, [isPlaying]);

  const closePlayer = useCallback(() => {
    audioRef.current.pause(); audioRef.current.src = ""; setCurrentStation(null); setIsPlaying(false);
  }, []);

  // Search
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try { const r = await fetch(`${API}/api/directory?q=${encodeURIComponent(searchQuery)}&limit=50`); const d = await r.json(); setSearchResults(d.listings || []); }
    catch { setSearchResults([]); } setSearching(false);
  }, [searchQuery]);

  useEffect(() => { const t = setTimeout(handleSearch, 400); return () => clearTimeout(t); }, [searchQuery, handleSearch]);

  // Genre filter — filter the existing allStations list (no duplicates)
  const handleGenreClick = (genre) => {
    if (activeGenre === genre) { setActiveGenre(null); setSearchResults(null); setSearchQuery(""); }
    else { setActiveGenre(genre); setSearchQuery(""); setSearchResults(null); }
  };

  // Filtered stations for the grid
  const gridStations = activeGenre
    ? allStations.filter(s => Array.isArray(s.genre) ? s.genre.includes(activeGenre) : s.genre === activeGenre)
    : allStations;

  const playingId = isPlaying && currentStation ? currentStation.id : null;

  return (
    <div className="min-h-screen bg-gray-950">
      <SEO
        title="Radio Directory — The NU Vibe Network"
        description="Browse live radio stations on The NU Vibe Network. Discover independent stations by genre, location, and style. Stream music 24/7 — Hip Hop, R&B, Jazz, Latin, Rock, and more."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "The NU Vibe Network Radio Directory",
          "description": "Live radio stations broadcasting on The NU Vibe Network",
          "url": window.location.href,
          "numberOfItems": allStations.length,
          "itemListElement": allStations.slice(0, 20).map((s, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "RadioStation",
              "name": s.station_name,
              "url": s.stream_url,
              "image": s.logo_url || undefined,
              "genre": Array.isArray(s.genre) ? s.genre.join(", ") : s.genre,
              "areaServed": s.location || undefined,
            }
          })),
        }}
      />
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/20 via-gray-950 to-gray-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><Radio className="w-5 h-5 text-emerald-400" /></div>
                <span className="text-emerald-400 text-sm font-medium tracking-wider uppercase">Radio Directory</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Online Radio Station Directory</h1>
              <p className="text-white/60 mt-2 text-sm sm:text-base max-w-2xl">
                Browse live internet radio stations on The NU Vibe Network &mdash; stream hip-hop, R&amp;B, Southern Soul, Jazz, Latin, Rock, Afrobeats, and Reggae 24/7 from independent DJs and broadcasters worldwide. Find a new favorite or <Link to="/directory/submit" className="text-emerald-400 hover:text-emerald-300 underline">list your own station</Link> to reach thousands of monthly listeners.
              </p>
              <p className="text-white/45 mt-2 text-xs sm:text-sm max-w-2xl">
                Prefer curated tracks? Explore <Link to="/discover" className="text-emerald-400 hover:text-emerald-300 underline">Discover</Link> for new uploads, check the <Link to="/network" className="text-emerald-400 hover:text-emerald-300 underline">Live Video Showcase</Link>, or meet the <Link to="/artists" className="text-emerald-400 hover:text-emerald-300 underline">artists</Link> behind the sound.
              </p>
            </div>
            <Link to="/directory/submit" className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-5 py-2.5 rounded-lg transition text-sm self-start sm:self-auto" data-testid="submit-station-btn">
              <Wifi className="w-4 h-4" /> Apply to Join The Network <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Search */}
          <div className="mt-8 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input type="text" placeholder="Search stations by name, genre, or location..." value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setActiveGenre(null); }}
              className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition text-sm"
              data-testid="directory-search" />
            {searching && <div className="absolute right-4 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>}
          </div>

          {/* Genre Chips */}
          {genres.length > 0 && (
            <div className="mt-4 flex gap-2 flex-wrap" data-testid="genre-chips">
              <button
                onClick={() => { setActiveGenre(null); setSearchQuery(""); setSearchResults(null); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                  !activeGenre && !searchResults
                    ? "bg-emerald-500 text-black border-emerald-500"
                    : "bg-white/5 text-white/60 hover:bg-emerald-500/20 hover:text-emerald-300 border-white/5 hover:border-emerald-500/30"
                }`} data-testid="genre-chip-all">
                All Stations
              </button>
              {genres.map((g) => (
                <button key={g} onClick={() => handleGenreClick(g)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                    activeGenre === g
                      ? "bg-emerald-500 text-black border-emerald-500"
                      : "bg-white/5 text-white/60 hover:bg-emerald-500/20 hover:text-emerald-300 border-white/5 hover:border-emerald-500/30"
                  }`} data-testid={`genre-chip-${g}`}>
                  {g}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 ${currentStation ? "pb-32" : "pb-20"}`}>
        {loading ? (
          <div className="flex items-center justify-center py-32"><Radio className="w-10 h-10 text-emerald-400 animate-pulse" /></div>
        ) : searchResults ? (
          /* Search Results */
          <div>
            <h2 className="text-white text-lg font-bold mb-6">{searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"</h2>
            {searchResults.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
                {searchResults.map((s) => <StationCard key={s.id} station={s} isPlaying={playingId === s.id} onPlay={handlePlay} myVote={myVotes[s.id]} voteCounts={allVoteCounts[s.id]} onVote={handleVote} />)}
              </div>
            ) : (
              <div className="text-center py-20">
                <Search className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <p className="text-white/40">No stations found.</p>
                <button onClick={() => { setSearchQuery(""); setSearchResults(null); }} className="mt-3 text-emerald-400 hover:text-emerald-300 text-sm">Clear search</button>
              </div>
            )}
          </div>
        ) : allStations.length === 0 ? (
          /* Empty */
          <div className="text-center py-32">
            <Radio className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-white text-xl font-bold mb-2">No stations yet</h2>
            <p className="text-white/40 mb-6 max-w-md mx-auto">Be the first to submit your radio station.</p>
            <Link to="/directory/submit" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-6 py-3 rounded-lg transition"><Wifi className="w-4 h-4" /> Apply to Join The Network</Link>
          </div>
        ) : (
          <>
            {/* Featured Carousel — only when viewing All (no genre filter active) */}
            {!activeGenre && featured.length > 0 && (
              <FeaturedCarousel stations={featured} playingId={playingId} onPlay={handlePlay} myVotes={myVotes} allVoteCounts={allVoteCounts} onVote={handleVote} />
            )}

            {/* Section Header */}
            <div className="flex items-center gap-2 mb-5">
              <Music className="w-5 h-5 text-emerald-400" />
              <h2 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                {activeGenre ? activeGenre : "All Stations"}
              </h2>
              <span className="text-white/30 text-sm ml-1">({gridStations.length})</span>
            </div>

            {/* Station Grid — ONE grid, no duplicates */}
            {gridStations.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5" data-testid="station-grid">
                {gridStations.map((s) => (
                  <StationCard key={s.id} station={s} isPlaying={playingId === s.id} onPlay={handlePlay} myVote={myVotes[s.id]} voteCounts={allVoteCounts[s.id]} onVote={handleVote} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Radio className="w-10 h-10 text-white/10 mx-auto mb-3" />
                <p className="text-white/30 text-sm">No stations in this genre yet.</p>
              </div>
            )}
          </>
        )}
      </div>

      <DirectoryMiniPlayer station={currentStation} playing={isPlaying} audioRef={audioRef} onToggle={togglePlay} onClose={closePlayer}
        myVote={currentStation ? myVotes[currentStation.id] : null} voteCounts={currentStation ? allVoteCounts[currentStation.id] : null} onVote={handleVote} />

      {/* Promo Toast — appears after 25s of listening */}
      {showPromo && (
        <div data-testid="promo-toast" className="fixed bottom-20 right-4 z-[70] w-[320px] bg-[#12121a]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-5 shadow-2xl shadow-black/40 animate-in slide-in-from-right"
          style={{ animation: "slideInRight 0.4s ease-out" }}>
          <button onClick={() => setShowPromo(false)} className="absolute top-3 right-3 text-white/20 hover:text-white/50 transition" data-testid="promo-dismiss">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Radio className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="text-white font-semibold text-sm leading-tight">{promoMessages[promoIndex].headline}</p>
          </div>
          <p className="text-white/40 text-xs mb-4 leading-relaxed">{promoMessages[promoIndex].sub}</p>
          <div className="flex items-center gap-2">
            <Link to="/directory/submit" onClick={() => setShowPromo(false)}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold py-2 rounded-lg text-center transition"
              data-testid="promo-start">
              Apply to Join The Network
            </Link>
            <a href="/#pricing" onClick={() => setShowPromo(false)}
              className="flex-1 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-xs font-medium py-2 rounded-lg text-center transition border border-white/5"
              data-testid="promo-plans">
              View Plans
            </a>
          </div>
        </div>
      )}
      <style>{`@keyframes slideInRight { from { opacity: 0; transform: translateX(100px); } to { opacity: 1; transform: translateX(0); } }`}</style>

      {/* Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
        <p className="text-white/30 text-sm text-center">Station logos and trademarks belong to their respective owners.</p>
      </div>

      <RelatedLinks keys={["discover", "artists", "network", "submit", "news", "advertise"]} heading="Keep Exploring" theme="emerald" />

      <Footer />
    </div>
  );
}
